import { RegisteredTag } from '@/models/DTO/registered-tag/registered-tag.model';
import { BaseAPIErrorModel } from '@/models/DTO/base-api-error.model';
import {
  Module,
  VuexModule,
  Action,
  Mutation,
  getModule
} from 'vuex-module-decorators';
import { Inject } from 'inversify-props';
import { RegisteredTagService } from '@/services/registered-tag.service';
import { RegisteredTagActions } from '@/constants/actions.constants';
import { RegisteredTagMutations } from '@/constants/mutation.constants';
import { RegisteredTagGetters } from '@/constants/getters.constants';
import { RegisteredTagRequest } from '@/models/DTO/registered-tag/registered-tag-request.model';
import { CheckIfTagExistsResponse } from '@/models/DTO/registered-tag/check-if-tag-exists-response.model';
import { UpdateRegisteredTagRequest } from '@/models/DTO/registered-tag/update-registered-tag-request.model';
import AuthenticationModule from './authentication.module';
import { Role } from '@/constants/role.constants';
import store from '../';
import { PayPalVenmoOrderDetails } from '@/models/paypal-venmo-order-details';

export const REGISTERED_TAG_MODULE_NAME = 'registeredTag';

class DefaultState {
  public registeredTags: RegisteredTag[] = [];
  public currentRegisteredTag: RegisteredTag | null = null;
  public errorResponse: BaseAPIErrorModel | null = null;
  public showLoading = false;
  public showButtonLoading = false;
  public showTableLoading = false;
  public doesTagExist: CheckIfTagExistsResponse | null = null;
  public orderDetails: PayPalVenmoOrderDetails | null = null;
}

const defaultState = new DefaultState();

@Module({
  namespaced: true,
  name: REGISTERED_TAG_MODULE_NAME
})
export default class RegisteredTagModule extends VuexModule {
  // #region Services
  @Inject()
  private readonly _registeredTagService!: RegisteredTagService;
  // #endregion

  // #region State
  public registeredTags: RegisteredTag[] = defaultState.registeredTags;
  public currentRegisteredTag: RegisteredTag | null =
    defaultState.currentRegisteredTag;
  public errorResponse: BaseAPIErrorModel | null = defaultState.errorResponse;
  public showLoading = defaultState.showLoading;
  public showButtonLoading = defaultState.showButtonLoading;
  public showTableLoading = defaultState.showTableLoading;
  public doesTagExist: CheckIfTagExistsResponse | null =
    defaultState.doesTagExist;
  public orderDetails: PayPalVenmoOrderDetails | null =
    defaultState.orderDetails;
  // #endregion

  // #region Getters
  public get [RegisteredTagGetters.hasErrorMessages](): boolean {
    return this.context.getters[RegisteredTagGetters.errorMessages].length > 0;
  }

  public get [RegisteredTagGetters.errorMessages](): string[] {
    return this.errorResponse?.getAllErrorMessages?.() ?? [];
  }
  // #endregion

  // #region Actions
  @Action
  public async [RegisteredTagActions.getTagByIdAction](
    id: string
  ): Promise<void> {
    try {
      this.context.commit(RegisteredTagMutations.showLoadingMutation, true);

      // remove any errors
      this.context.commit(RegisteredTagMutations.errorMutation, null);

      // remove current tag
      this.context.commit(
        RegisteredTagMutations.setCurrentRegisteredTagMutation,
        null
      );

      const resp = await this._registeredTagService.getTagById(id);

      // success
      this.context.commit(
        RegisteredTagMutations.setCurrentRegisteredTagMutation,
        {
          ...resp
        }
      );
    } catch (error) {
      console.log('module', error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(RegisteredTagMutations.errorMutation, e);
    } finally {
      this.context.commit(RegisteredTagMutations.showLoadingMutation, false);
    }
  }

  @Action
  public async [RegisteredTagActions.getTagByCodeAction](
    code: string
  ): Promise<void> {
    try {
      this.context.commit(RegisteredTagMutations.showLoadingMutation, true);

      // remove any errors
      this.context.commit(RegisteredTagMutations.errorMutation, null);

      // remove current tag
      this.context.commit(
        RegisteredTagMutations.setCurrentRegisteredTagMutation,
        null
      );

      const resp = await this._registeredTagService.getTagByCode(code);

      // success
      this.context.commit(
        RegisteredTagMutations.setCurrentRegisteredTagMutation,
        {
          ...resp
        }
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(RegisteredTagMutations.errorMutation, e);
    } finally {
      this.context.commit(RegisteredTagMutations.showLoadingMutation, false);
    }
  }

  @Action
  public async [RegisteredTagActions.isTagRegisteredAction](
    code: string
  ): Promise<void> {
    try {
      this.context.commit(RegisteredTagMutations.showLoadingMutation, true);

      // remove any errors
      this.context.commit(RegisteredTagMutations.errorMutation, null);

      // remove previous response
      this.context.commit(
        RegisteredTagMutations.setDoesTagExistsMutation,
        null
      );

      const resp = await this._registeredTagService.isTagRegistered(code);

      // success
      this.context.commit(RegisteredTagMutations.setDoesTagExistsMutation, {
        ...resp
      });
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(RegisteredTagMutations.errorMutation, e);
    } finally {
      this.context.commit(RegisteredTagMutations.showLoadingMutation, false);
    }
  }

  @Action
  public async [RegisteredTagActions.getAllTagsAction](): Promise<void> {
    try {
      this.context.commit(RegisteredTagMutations.showLoadingMutation, true);

      // remove any errors
      this.context.commit(RegisteredTagMutations.errorMutation, null);

      const resp = await this._registeredTagService.getAllTags();

      // success
      this.context.commit(RegisteredTagMutations.setRegisteredTagsMutation, [
        ...resp
      ]);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(RegisteredTagMutations.errorMutation, e);
    } finally {
      this.context.commit(RegisteredTagMutations.showLoadingMutation, false);
    }
  }

  @Action
  public async [RegisteredTagActions.getAllTagsByVeterinarianOfficeIdAction](
    id: string
  ): Promise<void> {
    try {
      this.context.commit(
        RegisteredTagMutations.showTableLoadingMutation,
        true
      );

      // remove any errors
      this.context.commit(RegisteredTagMutations.errorMutation, null);

      const resp = await this._registeredTagService.getAllTagsByVeterinarianOfficeId(
        id
      );

      // success
      this.context.commit(RegisteredTagMutations.setRegisteredTagsMutation, [
        ...resp
      ]);
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(RegisteredTagMutations.errorMutation, e);
    } finally {
      this.context.commit(
        RegisteredTagMutations.showTableLoadingMutation,
        false
      );
    }
  }

  @Action
  public async [RegisteredTagActions.addNewTagAction](
    payload: RegisteredTagRequest
  ): Promise<void> {
    try {
      this.context.commit(
        RegisteredTagMutations.showButtonLoadingMutation,
        true
      );

      // remove any errors
      this.context.commit(RegisteredTagMutations.errorMutation, null);

      // remove current tag
      this.context.commit(
        RegisteredTagMutations.setCurrentRegisteredTagMutation,
        null
      );

      let resp: RegisteredTag;

      const authModule: AuthenticationModule = getModule(
        AuthenticationModule,
        store
      );

      if (
        authModule.jwtRoles.length === 0 ||
        authModule.jwtRoles.includes(Role.Customer)
      ) {
        resp = await this._registeredTagService.addNewTagForCustomer({
          ...payload
        });
      } else {
        resp = await this._registeredTagService.addNewTag({
          ...payload
        });
      }

      // success
      this.context.commit(
        RegisteredTagMutations.setCurrentRegisteredTagMutation,
        {
          ...resp
        }
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(RegisteredTagMutations.errorMutation, e);
    } finally {
      this.context.commit(
        RegisteredTagMutations.showButtonLoadingMutation,
        false
      );
    }
  }

  @Action
  public async [RegisteredTagActions.updateTagAction](
    payload: UpdateRegisteredTagRequest
  ): Promise<void> {
    try {
      this.context.commit(
        RegisteredTagMutations.showButtonLoadingMutation,
        true
      );

      // remove any errors
      this.context.commit(RegisteredTagMutations.errorMutation, null);

      // remove current tag
      this.context.commit(
        RegisteredTagMutations.setCurrentRegisteredTagMutation,
        null
      );

      const resp = await this._registeredTagService.updateTag({
        ...payload
      });

      // success
      this.context.commit(
        RegisteredTagMutations.setCurrentRegisteredTagMutation,
        {
          ...resp
        }
      );
    } catch (error) {
      console.log(error.response.data);

      const e = new BaseAPIErrorModel(error.response.data);

      this.context.commit(RegisteredTagMutations.errorMutation, e);
    } finally {
      this.context.commit(
        RegisteredTagMutations.showButtonLoadingMutation,
        false
      );
    }
  }

  @Action
  public async [RegisteredTagActions.resetStateAction](): Promise<void> {
    this.context.commit(RegisteredTagMutations.resetStateMutation);
  }

  @Action
  public async [RegisteredTagActions.resetCurrentTagAction](): Promise<void> {
    this.context.commit(RegisteredTagMutations.resetCurrentTagMutation);
  }

  @Action
  public async [RegisteredTagActions.orderDetailsAction](
    payload: PayPalVenmoOrderDetails
  ): Promise<void> {
    this.context.commit(RegisteredTagMutations.orderDetailsMutation, payload);
  }
  // #endregion

  // #region Mutations
  @Mutation
  private [RegisteredTagMutations.setRegisteredTagsMutation](
    data: RegisteredTag[]
  ) {
    this.registeredTags = data;
  }

  @Mutation
  private [RegisteredTagMutations.setCurrentRegisteredTagMutation](
    data: RegisteredTag | null
  ) {
    this.currentRegisteredTag = data;
  }

  @Mutation
  private [RegisteredTagMutations.setDoesTagExistsMutation](
    data: CheckIfTagExistsResponse | null
  ) {
    this.doesTagExist = data;
  }

  @Mutation
  private [RegisteredTagMutations.showLoadingMutation](show: boolean) {
    this.showLoading = show;
  }

  @Mutation
  private [RegisteredTagMutations.showButtonLoadingMutation](show: boolean) {
    this.showButtonLoading = show;
  }

  @Mutation
  private [RegisteredTagMutations.showTableLoadingMutation](show: boolean) {
    this.showTableLoading = show;
  }

  @Mutation
  private [RegisteredTagMutations.errorMutation](
    errorModel: BaseAPIErrorModel | null
  ) {
    this.errorResponse = errorModel;
  }

  @Mutation
  private [RegisteredTagMutations.resetStateMutation]() {
    Object.assign(this, defaultState);
  }

  @Mutation
  private [RegisteredTagMutations.resetCurrentTagMutation]() {
    this.currentRegisteredTag = defaultState.currentRegisteredTag;
    this.doesTagExist = defaultState.doesTagExist;
  }

  @Mutation
  private [RegisteredTagMutations.orderDetailsMutation](
    data: PayPalVenmoOrderDetails
  ) {
    if (!data) {
      this.orderDetails = null;
    }

    this.orderDetails = { ...data };
  }
  // #endregion
}
